import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ParticleBackground from "../components/ParticleBackground";
import {
  Code2,
  Globe2,
  ShieldCheck,
  Bot,
  X,
  Send,
  Lightbulb,
  Star,
  Workflow,
  MessageCircle,
} from "lucide-react";

const services = [
  "Website Development",
  "Custom Software System",
  "Business Dashboard",
  "Booking System",
  "School Portal",
  "Healthcare System",
  "AI Chatbot Integration",
  "Secure Role-Based System",
];


function Home() {
  
  const [chatOpen, setChatOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [bookingMode, setBookingMode] = useState(false);
  const [bookingStep, setBookingStep] = useState(0);
  const [bookingData, setBookingData] = useState({
    service: "",
    name: "",
    contact: "",
    date: "",
    details: "",
  });

  const [messages, setMessages] = useState([
    {
      from: "bot",
      text: "Hi, I am ISSA AI 🤖. I can answer questions or help you book an Eiffeltech service step by step.",
    },
  ]);

  

  useEffect(() => {
    const openChat = () => setChatOpen(true);
    window.addEventListener("openAIChat", openChat);
    return () => window.removeEventListener("openAIChat", openChat);
  }, []);

 

  const normalReplies = {
    services: [
      "Eiffeltech offers website development, custom systems, dashboards, portals, booking systems and AI-powered platforms.",
      "We build modern websites, business systems, dashboards, school portals, healthcare systems and secure platforms.",
      "Our available services include web design, software development, AI tools, booking platforms and system automation.",
    ],
    contact: [
      "You can contact Eiffeltech on +263716292923 or email eiffel1@eiffeltech.co.zw.",
      "For enquiries, call +263716292923 or send an email to eiffel1@eiffeltech.co.zw.",
    ],
    default: [
      "Eiffeltech builds modern websites, business systems, dashboards, booking platforms and AI-powered digital tools.",
      "That is a good question. Eiffeltech focuses on secure, modern and intelligent digital solutions.",
      "We help organizations use technology to improve service delivery, management and growth.",
    ],
  };

  const randomReply = (category) => {
    const options = normalReplies[category] || normalReplies.default;
    return options[Math.floor(Math.random() * options.length)];
  };

  const sendBookingToWhatsApp = (data) => {
    const phone = "263716292923";
    const message = `Hello Eiffeltech, I want to book a service.

Service: ${data.service}
Name: ${data.name}
Contact: ${data.contact}
Preferred Date: ${data.date}
Details: ${data.details}`;

    window.open(
      `https://wa.me/${phone}?text=${encodeURIComponent(message)}`,
      "_blank"
    );
  };

  const startBooking = () => {
    setChatOpen(true);
    setBookingMode(true);
    setBookingStep(1);
    setBookingData({
      service: "",
      name: "",
      contact: "",
      date: "",
      details: "",
    });

    setMessages((prev) => [
      ...prev,
      {
        from: "bot",
        text:
          "Great! I will help you book an Eiffeltech service step by step. Which service do you want?\n\n" +
          services.map((s, i) => `${i + 1}. ${s}`).join("\n"),
      },
    ]);
  };

  const handleBookingStep = (userText) => {
    if (bookingStep === 1) {
      setBookingData((prev) => ({ ...prev, service: userText }));
      setBookingStep(2);
      return "Good choice. Please enter your full name.";
    }

    if (bookingStep === 2) {
      setBookingData((prev) => ({ ...prev, name: userText }));
      setBookingStep(3);
      return "Thank you. Please enter your phone number or email address.";
    }

    if (bookingStep === 3) {
      setBookingData((prev) => ({ ...prev, contact: userText }));
      setBookingStep(4);
      return "Great. What date would you prefer for the consultation or service discussion?";
    }

    if (bookingStep === 4) {
      setBookingData((prev) => ({ ...prev, date: userText }));
      setBookingStep(5);
      return "Almost done. Briefly describe what you want Eiffeltech to build for you.";
    }

    if (bookingStep === 5) {
      const finalData = {
        ...bookingData,
        details: userText,
      };

      setBookingData(finalData);
      setBookingMode(false);
      setBookingStep(0);
      sendBookingToWhatsApp(finalData);

      return `Booking prepared 

Service: ${finalData.service}
Name: ${finalData.name}
Contact: ${finalData.contact}
Preferred Date: ${finalData.date}
Details: ${finalData.details}

WhatsApp has opened with your booking message. Tap Send to submit it to Eiffeltech.`;
    }

    return "Let us continue your booking.";
  };

  const getBotReply = (text) => {
    const lower = text.toLowerCase();

    if (
      lower.includes("book") ||
      lower.includes("booking") ||
      lower.includes("appointment") ||
      lower.includes("consultation")
    ) {
      startBooking();
      return null;
    }

    if (
      lower.includes("service") ||
      lower.includes("offer") ||
      lower.includes("what do you do")
    ) {
      return randomReply("services");
    }

    if (
      lower.includes("contact") ||
      lower.includes("phone") ||
      lower.includes("email") ||
      lower.includes("call")
    ) {
      return randomReply("contact");
    }

    return randomReply("default");
  };

  const askBot = () => {
    if (!question.trim() || isTyping) return;

    const userText = question;

    setMessages((prev) => [...prev, { from: "user", text: userText }]);
    setQuestion("");
    setIsTyping(true);

    setTimeout(() => {
      const reply = bookingMode
        ? handleBookingStep(userText)
        : getBotReply(userText);

      if (reply) {
        setMessages((prev) => [...prev, { from: "bot", text: reply }]);
      }

      setIsTyping(false);
    }, 900 + Math.random() * 700);
  };

  const submitIdea = (e) => {
    e.preventDefault();

    const form = e.currentTarget;
    const name = form.name.value;
    const contact = form.contact.value;
    const ideaType = form.ideaType.value;
    const message = form.message.value;

    const whatsappMessage = `Hello Eiffeltech, I have a project idea or suggestion.

Name: ${name}
Contact: ${contact}
Type: ${ideaType}
Message: ${message}`;

    window.open(
      `https://wa.me/263716292923?text=${encodeURIComponent(
        whatsappMessage
      )}`,
      "_blank"
    );

    form.reset();
  };

return (
  <>
   <section className="relative flex min-h-[88vh] items-center justify-center overflow-hidden px-4 pb-10 pt-24 sm:min-h-screen sm:px-6 sm:pt-28">

  {/* PARTICLES */}
  <div className="absolute inset-0 z-0">
    <ParticleBackground />
  </div>

 

  {/* CONTENT */}
  <div className="relative z-20 mx-auto max-w-5xl text-center text-white">

    
        <motion.div
          initial={{ opacity: 0, y: 35 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <h1 className="text-4xl font-black leading-tight sm:text-6xl md:text-8xl">
            <span className="block text-white">EIFFELTECH</span>
            <span className="block text-cyan-400">DIGITAL</span>
            <span className="block text-white">SOLUTIONS</span>
          </h1>

          <p className="mx-auto mt-4 max-w-xl text-base font-semibold leading-7 text-slate-100 sm:mt-5 sm:text-xl md:max-w-3xl md:text-2xl">
            Building modern, secure and intelligent digital systems.
          </p>

          <div className="mt-6 flex flex-col justify-center gap-3 sm:mt-8 sm:flex-row sm:gap-4">
            <button
              onClick={startBooking}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/60 bg-black/10 px-5 py-3 text-sm font-bold backdrop-blur-md hover:border-cyan-400 hover:text-cyan-300 sm:px-7 sm:py-4 sm:text-base"
            >
              Book With ISSA AI <Bot size={18} />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  


      {/* HOW IT WORKS */}
      <section className="px-4 py-12 sm:px-6 sm:py-16 md:py-24">
        <div className="mx-auto max-w-7xl">
          <SectionHeader
            badgeIcon={<Workflow size={16} />}
            badgeText="Our Process"
            title="How Eiffeltech Works"
          />

          <div className="mt-8 grid grid-cols-1 gap-4 sm:mt-10 sm:grid-cols-2 lg:grid-cols-4">
            <ProcessCard number="01" title="Share Your Idea" text="Tell us what you want to build or improve." />
            <ProcessCard number="02" title="We Plan" text="We analyze requirements, features, and design direction." />
            <ProcessCard number="03" title="We Build" text="We develop the website, system, dashboard, or AI feature." />
            <ProcessCard number="04" title="Launch & Support" text="We help you launch and improve the system over time." />
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="px-4 py-12 sm:px-6 sm:py-16 md:py-24">
        <div className="mx-auto max-w-7xl">
          <SectionHeader
            title="Our Services"
            subtitle="Professional technology services for real business needs."
          />

          <div className="mt-8 grid grid-cols-1 gap-4 sm:mt-10 sm:grid-cols-2 md:grid-cols-3">
            <ServiceCard icon={<Globe2 />} title="Website Development" text="Modern responsive company websites with clean design and strong branding." />
            <ServiceCard icon={<Code2 />} title="Custom Software" text="School portals, business dashboards, healthcare systems and management platforms." />
            <ServiceCard icon={<ShieldCheck />} title="Secure Systems" text="Role-based access, authentication, database systems and secure deployments." />
          </div>
        </div>
      </section>


      {/* TESTIMONIALS */}
      <section className="px-4 py-12 sm:px-6 sm:py-16 md:py-24">
        <div className="mx-auto max-w-7xl">
          <SectionHeader title="What Clients Can Expect" />

          <div className="mt-8 grid grid-cols-1 gap-4 sm:mt-10 sm:grid-cols-2 md:grid-cols-3">
            <TestimonialCard text="Professional systems with clean design and strong functionality." name="Business Client" />
            <TestimonialCard text="Helpful support from planning to launch and future improvements." name="System User" />
            <TestimonialCard text="Smart AI booking and digital tools that improve service delivery." name="Service Provider" />
          </div>
        </div>
      </section>

      {/* PROJECT IDEAS */}
      <section className="px-4 py-12 sm:px-6 sm:py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 rounded-[1.5rem] border border-cyan-400/30 bg-slate-900/80 p-5 shadow-2xl backdrop-blur-xl bg-black/10 sm:gap-8 sm:rounded-[2rem] sm:p-8 md:grid-cols-2 md:p-12">
          <div>
            <Badge icon={<Lightbulb size={16} />} text="Share Your Idea" />
            <h2 className="text-2xl font-black leading-tight text-white sm:text-4xl md:text-5xl">
              Have a project idea or suggestion?
            </h2>

            <p className="mt-4 text-sm leading-7 text-slate-300 sm:mt-5 sm:text-lg sm:leading-8">
              Tell Eiffeltech what you want us to build, improve, or add. You
              can suggest a new project, request a feature, or share feedback.
            </p>
          </div>

          <form
            onSubmit={submitIdea}
            className="rounded-3xl border border-cyan-400/20 bg-slate-900/80 p-4 shadow-xl bg-slate-900/80 sm:p-6"
          >
            <input name="name" required placeholder="Your name" className="mb-3 w-full rounded-2xl border px-4 py-3 text-sm text-white-none sm:mb-4 sm:text-base" />
            <input name="contact" required placeholder="Phone number or email" className="mb-3 w-full rounded-2xl border px-4 py-3 text-sm text-white-none sm:mb-4 sm:text-base" />

            <select name="ideaType" required className="mb-3 w-full rounded-2xl border px-4 py-3 text-sm text-white-none sm:mb-4 sm:text-base">
              <option value="">Select type</option>
              <option value="Project Idea">Project Idea</option>
              <option value="Suggestion">Suggestion</option>
              <option value="Website Improvement">Website Improvement</option>
              <option value="Feature Request">Feature Request</option>
              <option value="General Feedback">General Feedback</option>
            </select>

            <textarea name="message" required rows="5" placeholder="Write your idea, suggestion, or improvement..." className="mb-3 w-full rounded-2xl border px-4 py-3 text-sm text-white-none sm:mb-4 sm:text-base" />

            <button type="submit" className="w-full rounded-full bg-cyan-400 px-5 py-3 text-sm font-bold text-slate-950 hover:bg-cyan-300 sm:px-7 sm:py-4 sm:text-base">
              Send Idea via WhatsApp
            </button>
          </form>
        </div>
      </section>

     

      {/* FINAL CTA */}
      <section className="px-4 py-12 sm:px-6 sm:py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-8 rounded-[1.5rem] border border-cyan-400/30 bg-slate-950 p-5 text-white shadow-[0_0_45px_rgba(34,211,238,0.25)] sm:rounded-[2rem] sm:p-10 md:grid-cols-2 md:p-16">
          <div className="text-center md:text-left">
            <h2 className="text-2xl font-black leading-tight text-white sm:text-4xl md:text-6xl">
              Ready to build your system?
            </h2>

            <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-300 sm:mt-5 sm:text-lg">
              Start with ISSA AI, share your project idea, or contact Eiffeltech
              directly for websites, dashboards, portals, and smart systems.
            </p>

            <div className="mt-6 flex flex-col gap-3 sm:mt-8 sm:flex-row md:justify-start">
              <button onClick={startBooking} className="inline-flex items-center justify-center gap-2 rounded-full bg-cyan-400 px-5 py-3 text-sm font-bold text-slate-950 hover:bg-cyan-300 sm:px-7 sm:py-4 sm:text-base">
                <Bot size={18} /> Start AI Booking
              </button>

              <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 px-5 py-3 text-sm font-bold hover:border-cyan-400 hover:text-cyan-300 sm:px-7 sm:py-4 sm:text-base">
                <MessageCircle size={18} /> Contact Us
              </Link>
            </div>
          </div>

          <div className="flex justify-center">
            <motion.button
              type="button"
              onClick={startBooking}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              animate={{ scale: [1, 1.04, 1], rotate: [0, 2, -2, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="relative flex h-44 w-44 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent outline-none sm:h-64 sm:w-64 md:h-72 md:w-72"
            >
              <motion.div
                animate={{
                  backgroundColor: [
                     "rgba(34,211,238,0.18)",
                     "rgba(59,130,246,0.18)",
                     "rgba(34,211,238,0.18)",
                  ],
                  scale: [1, 1.12, 1],
                }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute h-44 w-44 rounded-full shadow-2xl sm:h-64 sm:w-64 md:h-72 md:w-72"
              />

              <motion.div
                animate={{ scale: [1, 1.2, 1], opacity: [0.8, 0.35, 0.8] }}
                transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
                className="absolute h-36 w-36 rounded-full bg-cyan-400/30 blur-xl sm:h-52 sm:w-52 md:h-56 md:w-56"
              />

              <motion.div
                animate={{
                  y: [0, -10, 0],
                  backgroundColor: [
                    "rgb(34, 211, 238)",
                    "rgb(168, 85, 247)",
                    "rgb(59, 130, 246)",
                    "rgb(34, 211, 238)",
                  ],
                }}
                transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                className="relative flex h-28 w-28 items-center justify-center rounded-full text-slate-950 shadow-xl sm:h-40 sm:w-40 md:h-44 md:w-44"
              >
                <motion.div
                  animate={{ rotate: [0, 8, -8, 0], scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Bot className="h-14 w-14 sm:h-20 sm:w-20 md:h-[90px] md:w-[90px]" />
                </motion.div>
              </motion.div>
            </motion.button>
          </div>
        </div>
      </section>


{/* CHATBOT BOX */}
{chatOpen && (
  <div
  className="
    fixed inset-0 z-50
    flex items-end
    overflow-hidden
    bg-black/40 backdrop-blur-sm

    sm:justify-end
    sm:p-6
  "
>
    
    {/* MOBILE + DESKTOP CONTAINER */}
    <div
  className="
    flex flex-col overflow-hidden
    w-full

    h-[100dvh]
    max-h-[100dvh]

    bg-black
    bg-slate-900

    sm:h-[650px]
    sm:max-h-[650px]
    sm:max-w-sm
    sm:rounded-3xl
    sm:shadow-2xl
  "
>
      {/* HEADER */}
      <div className="flex items-center justify-between border-b border-cyan-400/20 p-4 border-slate-700">
        <div>
          <h3 className="text-lg font-black text-white">
            ISSA AI 🤖
          </h3>

          <p className="text-xs text-cyan-400 text-slate-400">
            Eiffeltech Assistant
          </p>
        </div>

        <button
          onClick={() => setChatOpen(false)}
          className="rounded-full p-2 hover:bg-slate-200 hover:bg-slate-700"
        >
          <X size={20} />
        </button>
      </div>

      {/* QUICK ACTIONS */}
      <div className="flex gap-2 overflow-x-auto px-3 py-3 scrollbar-hide">
        {["Services", "Pricing", "Book", "WhatsApp"].map((item) => (
          <button
            key={item}
            className="
              whitespace-nowrap
              rounded-full
              bg-cyan-400/10
              px-4 py-2
              text-xs font-bold
              text-cyan-400
            "
          >
            {item}
          </button>
        ))}
      </div>

      {/* CHAT AREA */}
      <div
  className="
    flex-1
    min-h-0
    overflow-y-auto
    overscroll-contain
    bg-black
    px-3 py-4
    bg-slate-800
  "
>
        <div className="space-y-3">
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`flex ${
                msg.from === "user"
                  ? "justify-end"
                  : "justify-start"
              }`}
            >
              <div
                className={`
                  max-w-[85%]
                  rounded-2xl
                  px-4 py-3
                  text-sm
                  leading-6
                  whitespace-pre-line
                  break-words
                  shadow-sm

                  ${
                    msg.from === "bot"
                      ? "bg-black text-white bg-slate-700 text-white"
                      : "bg-cyan-400 text-slate-950"
                  }
                `}
              >
                {msg.text}
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="flex justify-start">
              <div className="rounded-2xl bg-black px-4 py-3 text-sm text-white shadow-sm bg-slate-700 text-white">
                ISSA is typing...
              </div>
            </div>
          )}
        </div>
      </div>

      {/* INPUT AREA */}
      <div className="shrink-0 border-t border-cyan-400/20 bg-black p-3 border-slate-700 bg-slate-900">
        <div className="flex items-center gap-2">
          <input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && askBot()}
            placeholder="Ask or book a service..."
            className="
              flex-1
              rounded-full
              border
              border-cyan-400/30
              bg-black
              px-4 py-3
              text-base
              outline-none
              focus:border-cyan-400
            "
          />

          <button
            onClick={askBot}
            className="
              flex h-12 w-12 shrink-0
              items-center justify-center
              rounded-full
              bg-cyan-400
              text-slate-950
              shadow-lg
            "
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  </div>
)}
          </>
  );
}
      
function Badge({ icon, text }) {
  return (
    <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-cyan-400 px-3 py-2 text-[11px] font-bold text-cyan-500 sm:px-4 sm:text-sm">
      {icon} {text}
    </div>
  );
}

function SectionHeader({ badgeIcon, badgeText, title, subtitle }) {
  return (
    <div className="mx-auto max-w-3xl text-center">
      {badgeText && <Badge icon={badgeIcon} text={badgeText} />}
      <h2 className="text-2xl font-black leading-tight text-white sm:text-4xl md:text-5xl">
        {title}
      </h2>
      {subtitle && (
        <p className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-white text-slate-300 sm:mt-4 sm:text-base">
          {subtitle}
        </p>
      )}
    </div>
  );
}



function ProcessCard({ number, title, text }) {
  return (
    <motion.div
      whileHover={{ y: -6 }}
      className="rounded-3xl border border-cyan-400/20 bg-slate-900/80 p-5 shadow-xl backdrop-blur-xl bg-black/10 sm:p-8"
    >
      <div className="mb-4 text-3xl font-black text-cyan-400 sm:mb-5 sm:text-4xl">
        {number}
      </div>
      <h3 className="text-lg font-black text-white sm:text-xl">{title}</h3>
      <p className="mt-3 text-sm leading-7 text-slate-300 sm:text-base">
        {text}
      </p>
    </motion.div>
  );
}

function TestimonialCard({ text, name }) {
  return (
    <motion.div
      whileHover={{ y: -6 }}
      className="rounded-3xl border border-cyan-400/20 bg-slate-900/80 p-5 shadow-xl backdrop-blur-xl bg-black/10 sm:p-8"
    >
      <div className="mb-4 flex gap-1 text-cyan-400 sm:mb-5">
        {[1, 2, 3, 4, 5].map((item) => (
          <Star key={item} size={17} fill="currentColor" />
        ))}
      </div>

      <p className="text-sm leading-7 text-slate-300 sm:text-base">
        “{text}”
      </p>
      <h3 className="mt-5 font-black text-white">{name}</h3>
    </motion.div>
  );
}

function ServiceCard({ icon, title, text }) {
  return (
    <motion.div
      whileHover={{ y: -6 }}
      className="rounded-3xl border border-cyan-400/20 bg-slate-900/80 p-5 shadow-xl backdrop-blur-xl bg-black/10 sm:p-8"
    >
      <div className="mb-4 text-cyan-400 sm:mb-5">{icon}</div>
      <h3 className="text-lg font-black text-white sm:text-xl">{title}</h3>
      <p className="mt-3 text-sm leading-7 text-slate-300 sm:text-base">
        {text}
      </p>
    </motion.div>
  );
}

export default Home;
